// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { Resources } from "../Commons/properties/Resources.js";
import { Message } from "../Vendors/PFSVendor/WebFix/Messages/Message.ts";
import { PFSVendor } from "../Vendors/PFSVendor/PFSVendor.js"
import { CustomEvent } from "../Utils/CustomEvents.js";
import { messageBoxHandler } from "../Utils/AppHandlers.js"
import { ErrorInformationStorage } from "./ErrorInformationStorage.js";


let _Connection = function (dataCache, funcBeforeDisconnectCallback)
{
    this.vendor = null;
    this.vendorMsgListener = null
    this.dataCache = dataCache;
    this.connectionState = ConnectionStates.DISCONNECTED;
    this.onConnectStateChange = new CustomEvent();

    this.ConnectResultData = null;
    this.Reconnecting = false;

    this.lastConnectionAttemptResponse = { text: '' };
    this.RefreshAccessTokenInterval = null;

    this.funcBeforeDisconnectCallback = funcBeforeDisconnectCallback;
}

_Connection.prototype.connect = function (connectParams, fromReconnect)
{
    let vendor = null;

    if (!this.Reconnecting)
        this.ConnectResultData = null;

    vendor = this.createPFSVendor();

    if (this.vendor)
        this.ClearPreviousVendorRefs();

    this.vendor = vendor;

    if (this.vendorMsgListener)
        this.SubscribeOnFullLoging(this.vendorMsgListener);
    else
        this.UnSubscribeOnFullLoging();

    let dc = this.dataCache;
    // dc.cleanup();

    vendor.Test = _Connection.Test++;
    let qc = dc.FQuoteCache;
    qc.onRequestQuotesSubscription.Subscribe(vendor.SubscribeSymbol, vendor);
    qc.onCancelQuotesSubscription.Subscribe(vendor.UnsubscribeSymbol, vendor);

    vendor.AddMessageListener(dc.NewMessage, dc);
    vendor.AddConnectedListener(this.OnVendorConnected, this);
    vendor.AddDisconnectedListener(this.OnVendorDisconnected, this);
    vendor.AddConnectionLostListener(this.OnConnectionLost, this);
    vendor.AddOnErrorListener(this.OnError, this);

    this.lastConnectionAttemptResponse = { text: '' };

    if (this.Reconnecting)
        return this.RefreshAccessToken(fromReconnect)
            .then(function (ConnectResultData)
            {
                if (!this.Reconnecting)
                    return Promise.resolve(true);

                if (ConnectResultData.Valid && ConnectResultData.StreamOpened)
                    return this._connectProcess(vendor, connectParams)
                else if (!ConnectResultData.Valid && ConnectResultData.StreamOpened)
                {
                    this.lastConnectionAttemptResponse = { text: Resources.getResource("Session has expired. Please login.") };
                    this.disconnect();
                    return Promise.resolve(false);
                }

                if (fromReconnect && !ConnectResultData.StreamOpened)
                    return Promise.resolve([false, true]);

            }.bind(this));
    else
        return this._connectProcess(vendor, connectParams)
};

_Connection.prototype._connectProcess = function (vendor, connectParams)
{
    return vendor.Connect(connectParams).then(function (ConnectResultData)
    {
        let data = {};
        this.Reconnecting = false;

        data.ConnectParams = connectParams;
        data.ConnectResultData = ConnectResultData;

        //value in seconds
        if (ConnectResultData.accessTokenLifeTime)
        {
            if (this.RefreshAccessTokenInterval)
                clearInterval(this.RefreshAccessTokenInterval);
            this.RefreshAccessTokenInterval = null;

            //15 seconds before
            let timeout = (ConnectResultData.accessTokenLifeTime - 15) * 1000;
            this.RefreshAccessTokenInterval = setTimeout(this.RefreshAccessToken.bind(this), timeout);
        }
        this.ConnectResultData = data;
        return ConnectResultData.Connected;
    }.bind(this))
}

_Connection.prototype.OnVendorConnected = function (connectedArguments)
{
    if (connectedArguments.isPasswordExpired)
    {
        this.lastConnectionAttemptResponse = { text: '', UserID: connectedArguments.UserID, isPasswordExpired: connectedArguments.isPasswordExpired, ConnectReason: connectedArguments.ConnectionStateCode };
        this.SetConnectionState(ConnectionStates.HALFCONNECTED);
    }
    else
        this.SetConnectionState(ConnectionStates.CONNECTED);
};

_Connection.prototype.disconnect = function (isNoNActivity = false)
{
    if (this.funcBeforeDisconnectCallback)
        this.funcBeforeDisconnectCallback();

    this.Reconnecting = false;
    if (this.vendor)
        this.vendor.Disconnect({ isNoNActivity: isNoNActivity });
};

_Connection.prototype.ClearPreviousVendorRefs = function ()
{
    let vendor = this.vendor;

    let dc = this.dataCache;
    let qc = dc.FQuoteCache;

    dc.cleanup();   // не сможем отписаться от котировок (например в алертах) во время очистки dataCache если до этого отпишемся от vendor.UnsubscribeSymbol

    qc.onRequestQuotesSubscription.UnSubscribe(vendor.SubscribeSymbol, vendor);
    qc.onCancelQuotesSubscription.UnSubscribe(vendor.UnsubscribeSymbol, vendor);

    vendor.RemoveMessageListener(dc.NewMessage, dc);
    vendor.RemoveConnectedListener(this.OnVendorConnected, this);
    vendor.RemoveDisconnectedListener(this.OnVendorDisconnected, this);
    vendor.RemoveConnectionLostListener(this.OnConnectionLost, this);
    vendor.RemoveOnErrorListener(this.OnError, this);
}

_Connection.prototype.OnError = function ()
{
    let disconectArguments = {
        ErrorInConnection: true,
        Text: Resources.getResource('connect.connection.unknown.error')
    }

    this.OnVendorDisconnected(disconectArguments)   // предотвращаем зависание на Connecting
}

_Connection.prototype.OnVendorDisconnected = function (disconectArguments)
{
    this.ClearPreviousVendorRefs();

    if (disconectArguments)
    {
        if (disconectArguments.LogoutType === 1 && (this.connectionState === ConnectionStates.CONNECTION_LOST || this.connectionState === ConnectionStates.CONNECTED))
        {
            this.OnConnectionLost()
            return;
        }

        if (disconectArguments.verificationMode)
        {
            this.lastConnectionAttemptResponse = { text: "", connectReason: ConectionResultEnum.AdditionalVerification, verificationModeCode: disconectArguments.verificationMode };
            this.SetConnectionState(ConnectionStates.DISCONNECTED, true);
            return;
        }
        switch (disconectArguments.ErrorCode)
        {
            case ERROR_MULTI_POLICY:
                messageBoxHandler.Show(Resources.getResource('panel.EventLog.eventType.disconnect'), Resources.getResource(disconectArguments.Text), messageBoxHandler.msgType.Warning, null, null, false, true, "", { okText: Resources.getResource('general.messageBox.ok') }, true);
                break;

            case ERROR_NEED_ACTIVATION:
                this.lastConnectionAttemptResponse = { text: disconectArguments.Text, connectReason: ConectionResultEnum.NeedActivation };
                break;

            case INVALID_BRANDING_KEY:
                this.lastConnectionAttemptResponse = { text: disconectArguments.Text, ErrorCode: disconectArguments.ErrorCode, connectReason: ConectionResultEnum.InvalidBrandingKey };
                break;

            default:
                if (disconectArguments.Text)
                    this.lastConnectionAttemptResponse = { text: disconectArguments.Text, ErrorCode: disconectArguments.ErrorCode };
        }
    }

    this.SetConnectionState(ConnectionStates.DISCONNECTED, disconectArguments ? disconectArguments.ErrorInConnection : false);
};

_Connection.prototype.OnConnectionLost = function ()
{
    this.Reconnecting = true;
    if (this.RefreshAccessTokenInterval)
        clearInterval(this.RefreshAccessTokenInterval);
    this.RefreshAccessTokenInterval = null;
    this.SetConnectionState(ConnectionStates.CONNECTION_LOST);
}

_Connection.prototype.SetConnectionState = function (state, forse)
{
    let needEvent = this.connectionState !== state;
    this.connectionState = state;
    if (needEvent || forse)
        this.onConnectStateChange.Raise(this);
};

_Connection.prototype.getState = function ()
{
    return this.connectionState;
};

_Connection.prototype.isDisconnected = function ()
{
    return this.connectionState === ConnectionStates.DISCONNECTED;
};

_Connection.prototype.isConnectionCorrect = function ()
{
    return this.connectionState !== ConnectionStates.DISCONNECTED && this.connectionState !== ConnectionStates.CONNECTION_LOST;
};

_Connection.prototype.getPackIn = function ()
{
    if (this.vendor)
        return this.vendor.in;
    else
        return 0;
};

_Connection.prototype.getPackOut = function ()
{
    if (this.vendor)
        return this.vendor.out;
    else
        return 0;
};

_Connection.prototype.SubscribeOnFullLoging = function (handler)
{
    if (this.vendor && this.vendor.SubscribeOnFullLoging)
        this.vendor.SubscribeOnFullLoging(handler);

    this.vendorMsgListener = handler;
}

_Connection.prototype.UnSubscribeOnFullLoging = function ()
{
    if (this.vendor && this.vendor.UnSubscribeOnFullLoging)
        this.vendor.UnSubscribeOnFullLoging();

    this.vendorMsgListener = null;
}

_Connection.prototype.GetMessagesList = function ()
{
    //TODO
    return Message.CodesString;
    //if (this.vendor && this.vendor.GetMessagesList)
    //    return this.vendor.GetMessagesList();
}

_Connection.prototype.SendPing = function ()
{
    let startTime = new Date().getTime();
    if (this.vendor)
        return this.vendor.SendPing().then(function ()
        {
            let finishTime = new Date().getTime();
            let diff = finishTime - startTime;
            return diff;
        });
}

_Connection.prototype.RefreshAccessToken = function (fromReconnect)
{
    if (!this.ConnectResultData)
        return Promise.resolve(false);

    return this.vendor.RefreshAccessToken(this.ConnectResultData, fromReconnect)
        .then(function (TokenDataObj)
        {
            if (!TokenDataObj.StreamOpened)
                return TokenDataObj

            if (!TokenDataObj.Valid)
                return TokenDataObj

            let data = this.ConnectResultData;
            let CP = data.ConnectParams
            let CRD = data.ConnectResultData

            CP.accessToken = CRD.accessToken = TokenDataObj.accessToken;
            CRD.accessTokenLifeTime = TokenDataObj.accessTokenLifeTime;
            CRD.refreshToken = TokenDataObj.refreshToken;
            CRD.refreshTokenLifeTime = TokenDataObj.refreshTokenLifeTime;

            //value in seconds
            if (TokenDataObj.accessTokenLifeTime && !this.Reconnecting)
            {
                //15 seconds before
                let timeout = (TokenDataObj.accessTokenLifeTime - 15) * 1000;
                //anti Ira defence
                if (timeout >= 2147483647) // limits it to (2^31)-1 ms (2147483647 ms)
                    timeout = 2147483640 // well, to be sure

                this.RefreshAccessTokenInterval = setTimeout(this.RefreshAccessToken.bind(this), timeout);
            }

            return { Valid: true, StreamOpened: true }

        }.bind(this)).catch(function ()
        {
            console.log("Refresh Access Token FAILED!!!!!!!")
            return { Valid: false, StreamOpened: false, FatalError: true };
        });
}

_Connection.prototype.AccountOperationRequest = function (data)
{
    if (this.vendor)
        return this.vendor.AccountOperationRequest(data);
    return Promise.resolve(null);
}

_Connection.prototype.ChangePassword = function (curPwd, newPwd, UserID, verificationPassword)
{
    const accessToken = this.ConnectResultData && this.ConnectResultData.ConnectParams ? this.ConnectResultData.ConnectParams.accessToken : '';

    if (this.vendor)
        return this.vendor.ChangePassword(curPwd, newPwd, UserID, verificationPassword, accessToken);
    return Promise.resolve(null);
}

_Connection.prototype.ChangeTradingPassword = function (curPwd, newPwd)
{
    if (this.vendor)
        return this.vendor.ChangeTradingPassword(curPwd, newPwd);
    return Promise.resolve(null);
}

_Connection.prototype.SendSOAPRequest = function (request, connectTo)
{
    return PFSVendor.SendSOAPRequest(request, connectTo, this.vendorMsgListener);
}

// msgType - name of method. like SendRequestRecoveryPassword, SendPasswordRecoveryChangeRequest etc
_Connection.prototype.SendMessageToVendor = function (url, isSSL, msgType, dataForMsg)
{
    return PFSVendor.SendMessageToVendor(url, isSSL, msgType, dataForMsg, this.vendorMsgListener)
}

_Connection.prototype.GetFirstTradeStreamData = function (curPwd, newPwd)
{
    if (this.vendor)
        return this.vendor.GetFirstTradeStreamData(curPwd, newPwd);
    return null;
}

// #region Vendor Factory

_Connection.prototype.createPFSVendor = function ()
{
    return new PFSVendor();
};

// #endregion Vendor Factory

// #region Enums

export let ConnectionStates = {
    /// Подлючен
    CONNECTED: "connected.ok",
    /// Подключается
    CONNECTING: "connecting",
    /// Отключен
    DISCONNECTED: "disconnected",
    /// Частично подключен
    HALFCONNECTED: "halfconnected",
    /// Потеря соединения
    CONNECTION_LOST: "connection.lost"
};
Object.freeze(ConnectionStates);

_Connection.DisconnectType = {
    Unspecified: 0,
    DisconnectedByServer: 1,
    ConnectionLost: 2,
    DisconnectedByClient: 3,
};
Object.freeze(_Connection.DisconnectType);

_Connection.DisconnectLevel = {
    Unspecified: 0,
    ConsoleToJava: 1,
    AspToConsole: 2,
    JavascriptToAsp: 3,
    CloseSocket: 4
};
Object.freeze(_Connection.DisconnectLevel);

export let ConectionResultEnum = {
    DisConnected: 0,
    Connected: 1,
    Error: 2,
    NeedActivation: 3,
    AdditionalVerification: 4,
    NeedChangePassword: 5,
    InvalidBrandingKey: 6
};
Object.freeze(ConectionResultEnum);

export const ERROR_NONE = 0;
export const ERROR_NEED_ACTIVATION = 1;
export const ERROR_WRONG_ACTIVATION = 2;
export const ERROR_INTERNAL = 3;
export const ERROR_MAINTENANCE = 4;
export const ERROR_USER_BLOCKED = 5;
export const ERROR_MULTI_POLICY = 6;
export const ERROR_NOT_ALLOWED = 7;
export const ERROR_USER_NOT_FOUND = 8;
export const INVALID_LOGIN_PASSWORD_COMBINATION = 9;
export const INVALID_BRANDING_KEY = 10;

export const ERROR_UNKNOWN = 13;
export const ERROR_NO_WORKING_SERVERS = 14;
// #endregion Enums


_Connection.prototype.GetHistoryPromise = async function (historyParams, signal)
{
    const vndr = this.vendor;
    if (!vndr)
    {
        return [];
    }

    const intervals = await vndr.GetHistoryPromise(historyParams, this.ConnectResultData, signal);
    return intervals;
}
_Connection.Test = 1;

/// <summary>
/// пользователь заблокирован после брутфорс атаки, время до авторазблокировки НЕ доступно (авторазблокировка не отработает) - обычно после рестарта 
/// </summary>
_Connection.ERROR_LOCKED_AFTER_BRUTEFORCE_PERMANENT = 195;

/// <summary>
/// пользователь заблокирован после брутфорс атаки, время до авторазблокировки доступно
/// </summary>
_Connection.ERROR_LOCKED_AFTER_BRUTEFORCE_TIME = 215;

/// <summary>
/// у пользователя установлена блокировка Locked в БО
/// </summary>
_Connection.ERROR_LOCKED_BACKOFFICE = 214;
_Connection.prototype.getLocalizedConnectionErrorString = function (LoginName)
{
    var rawString = this.lastConnectionAttemptResponse.text;
    // let code = null;
    let JSONParsed = null;
    if (rawString)
    {
        try
        {
            JSONParsed = JSON.parse(rawString);
            rawString = JSONParsed.message;
            // code = rawString.code || null
        } catch (e)
        {
            JSONParsed = null;
            ErrorInformationStorage.GetException(e);
        }
    }
    if (rawString === "password.expired")
        return "";
    if (rawString === "additional.verification")
        return Resources.getResource("screen.licensing.statusMessages.connecting");
    let locString = "";


    locString = Resources.getResource(rawString)
    let att_index = rawString.indexOf("Attempt")
    if (att_index !== -1)
    {
        let att_c = rawString.substring(att_index + 8).trim();
        locString = Resources.getResource("general.connection.Login/password combination is not valid. Attempt").replace("{0}", att_c);
    }
    else
    {
        let tmp_rawString = "general.connection." + rawString
        if (Resources.IsResourcePresent(tmp_rawString))
            locString = Resources.getResource(tmp_rawString);
        else
        {
            tmp_rawString = "general.connection." + rawString + "."
            if (Resources.IsResourcePresent(tmp_rawString))
                locString = Resources.getResource(tmp_rawString);
        }
    }

    if (JSONParsed && JSONParsed.code)
    {
        let code = parseInt(JSONParsed.code)
        switch (code)
        {
            case _Connection.ERROR_LOCKED_AFTER_BRUTEFORCE_PERMANENT:
                locString = Resources.getResource("User is locked after brute force!");
                break;
            case _Connection.ERROR_LOCKED_AFTER_BRUTEFORCE_TIME:
                locString = Resources.getResource("screen.LoginScreen.responceErrors.bruteforceBlockedMinutes").replace("{1}", LoginName).replace("{2}", JSONParsed.minutes)
                break;
            case _Connection.ERROR_LOCKED_BACKOFFICE:
                locString = Resources.getResource("User is blocked. Please contact support.");
                break;
        }
    }
    return locString;
};

export let Connection = null;

export function ConnectionInstanceCreator (dataCache, funcBeforeDisconnectCallback) 
{
    Connection = new _Connection(dataCache, funcBeforeDisconnectCallback);
}

let _ConnectionInfo = function ()
{
    this.ConnectionInfoArray = [];
    this.OnConnectionInfoArrayChanged = new CustomEvent();
}

export const ConntionInfo = new _ConnectionInfo();